import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronDown, ChevronRight, Scale } from "lucide-react";
import { toast } from "sonner";
import { inr, formatDate, todayISO } from "@/lib/utils2";
import BankSelector from "@/components/BankSelector";
import SettleDialog from "@/components/SettleDialog";

const empty = { transporter_id: "", transporter_name: "", date: todayISO(), amount: 0, mode: "Bank", bank_account_id: "", particulars: "", allocations: [] };

export default function PaymentsMade() {
  const [payments, setPayments] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [allTrucks, setAllTrucks] = useState([]);
  const [parties, setParties] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [expanded, setExpanded] = useState({});
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleTrans, setSettleTrans] = useState("");
  const [period, setPeriod] = usePersistedState("period:pm", () => presetRange("this-month"));

  const load = async () => {
    const params = { date_from: period.from, date_to: period.to };
    const [p, tt, tr, pp, mm] = await Promise.all([
      api.get("/payments-made", { params }), api.get("/transporters"),
      api.get("/trucks"), api.get("/parties"), api.get("/materials"),
    ]);
    setPayments(p.data); setTransporters(tt.data); setAllTrucks(tr.data); setParties(pp.data); setMaterials(mm.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period.from, period.to]);

  const tname = (id) => transporters.find((t) => t.id === id)?.name || "";
  const pname = (id) => parties.find((p) => p.id === id)?.name || "";
  const mname = (id) => materials.find((m) => m.id === id)?.name || "";

  // Outstanding trucks for selected transporter — uses total_owed_to_transporter
  // (covers material + transport + purchase GST, and works for combined-purchase trucks).
  const outstandingTrucks = useMemo(() => {
    if (!form.transporter_id) return [];
    // Compute paid per truck (across all existing payments — exclude the payment we're editing)
    const paidByTruck = {};
    payments.forEach((p) => {
      if (p.id && p.id === form.id) return; // exclude self when applying on-account
      (p.allocations || []).forEach((a) => {
        if (a.truck_id) paidByTruck[a.truck_id] = (paidByTruck[a.truck_id] || 0) + Number(a.amount);
      });
    });
    return allTrucks
      .filter((t) => t.transporter_id === form.transporter_id)
      .map((t) => {
        const owed = Number(
          t.total_owed_to_transporter
            ?? ((t.material_purchase_amount || 0) + (t.transport_purchase_amount || 0) + (t.purchase_gst_amount || 0))
        );
        return { ...t, owed, paid: paidByTruck[t.id] || 0, balance: owed - (paidByTruck[t.id] || 0) };
      })
      .filter((t) => t.owed > 0 && t.balance > 0.01)
      .sort((a, b) => (a.entry_date < b.entry_date ? -1 : 1));
  }, [form.transporter_id, form.id, allTrucks, payments]);

  const allocTotal = useMemo(() =>
    form.allocations.reduce((s, a) => s + (Number(a.amount) || 0), 0), [form.allocations]);
  const onAccount = (Number(form.amount) || 0) - allocTotal;

  const toggleAlloc = (truck, checked) => {
    if (checked) {
      // Never go minus: cap at money still unallocated on this payment
      const amt = Math.round(Math.min(truck.balance, Math.max(onAccount, 0)) * 100) / 100;
      setForm((s) => ({ ...s, allocations: [...s.allocations, { truck_id: truck.id, amount: amt }] }));
    } else {
      setForm((s) => ({ ...s, allocations: s.allocations.filter((a) => a.truck_id !== truck.id) }));
    }
  };
  const updateAllocAmt = (truck_id, amt) => {
    setForm((s) => ({
      ...s,
      allocations: s.allocations.map((a) => a.truck_id === truck_id ? { ...a, amount: parseFloat(amt) || 0 } : a),
    }));
  };

  const save = async () => {
    if (!form.amount) return toast.error("Amount required");
    if (allocTotal > Number(form.amount) + 0.01) return toast.error(`Allocations ${inr(allocTotal)} exceed payment amount ${inr(form.amount)} — reduce a pay amount or use "Settle Trucks" to combine several payments`);
    const payload = {
      ...form, amount: Number(form.amount),
      transporter_id: form.transporter_id || null,
      allocations: form.allocations.filter((a) => a.amount > 0),
    };
    try {
      if (form.id) {
        await api.put(`/payments-made/${form.id}`, payload);
        toast.success("On-account balance applied");
      } else {
        await api.post("/payments-made", payload);
        toast.success("Payment recorded");
      }
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Re-open dialog in "Apply on-account" mode against an existing payment.
  const applyOnAccount = (p) => {
    setForm({
      id: p.id,
      transporter_id: p.transporter_id || "",
      transporter_name: p.transporter_name || "",
      date: p.date,
      amount: p.amount,
      mode: p.mode || "Bank",
      bank_account_id: p.bank_account_id || "",
      particulars: p.particulars || "",
      allocations: (p.allocations || []).map((a) => ({ truck_id: a.truck_id, amount: Number(a.amount) || 0 })),
    });
    setOpen(true);
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] mx-auto" data-testid="payments-made-page">
      <PageHeader
        title="Payments Made"
        subtitle="Debits to transporters / vendors — allocate against specific truck trips, or keep on-account."
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="settle-trucks-btn" variant="outline" onClick={() => { setSettleTrans(""); setSettleOpen(true); }}
              className="rounded-sm border-brand-sand text-brand-ink hover:bg-brand-sand/10">
              <Scale className="h-4 w-4 mr-1.5" /> Settle Trucks
            </Button>
            <Button data-testid="add-payment-made-btn" onClick={() => { setForm({ ...empty, date: todayISO() }); setOpen(true); }}
              className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Record Payment
            </Button>
          </div>
        }
      />
      <Card className="rounded-sm border-brand-line shadow-none p-4 mb-5">
        <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
          defaultPreset="this-month" testIdPrefix="pm-period" />
      </Card>
      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="w-8 px-3 py-3"></th>
              <th className="text-left px-3 py-3">Date</th>
              <th className="text-left px-3 py-3">Transporter</th>
              <th className="text-left px-3 py-3">Mode</th>
              <th className="text-left px-3 py-3">Particulars</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-center px-3 py-3">Trucks Settled</th>
              <th className="text-right px-3 py-3">On Account</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-brand-muted">No payments yet.</td></tr>}
            {payments.map((p) => {
              const isOpen = expanded[p.id];
              const allocs = p.allocations || [];
              return (
                <React.Fragment key={p.id}>
                  <tr className="border-t border-brand-line hover:bg-secondary/40">
                    <td className="px-3 py-3">
                      {allocs.length > 0 && (
                        <button onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))} className="text-brand-muted hover:text-brand-ink">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3">{formatDate(p.date)}</td>
                    <td className="px-3 py-3 font-medium">{tname(p.transporter_id) || p.transporter_name || "—"}</td>
                    <td className="px-3 py-3 text-brand-muted">{p.mode}</td>
                    <td className="px-3 py-3 text-brand-muted">{p.particulars}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-brand-terracotta font-medium">{inr(p.amount)}</td>
                    <td className="px-3 py-3 text-center">
                      {allocs.length > 0
                        ? <Badge className="bg-brand-forest text-white rounded-sm">{allocs.length} truck{allocs.length > 1 ? "s" : ""}</Badge>
                        : <span className="text-xs text-brand-muted">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-brand-sand">{p.on_account_amount > 0 ? inr(p.on_account_amount) : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {p.on_account_amount > 0.01 && (
                          <>
                            <Button size="sm" variant="outline"
                              className="h-7 rounded-sm text-[11px] border-brand-sand text-brand-sand hover:bg-brand-sand/10"
                              onClick={() => applyOnAccount(p)}
                              data-testid={`apply-onacc-${p.id}`}
                            >
                              Apply {inr(p.on_account_amount)}
                            </Button>
                            {p.transporter_id && (
                              <Button size="sm" variant="ghost"
                                className="h-7 rounded-sm text-[11px] text-brand-forest"
                                onClick={() => { setSettleTrans(p.transporter_id); setSettleOpen(true); }}
                                data-testid={`settle-made-${p.id}`}
                              >
                                <Scale className="h-3 w-3 mr-1" /> Settle
                              </Button>
                            )}
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta"
                          onClick={async () => { if (confirm("Delete?")) { await api.delete(`/payments-made/${p.id}`); load(); } }}
                          data-admin-only="true">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && allocs.length > 0 && (
                    <tr className="bg-secondary/40">
                      <td colSpan={9} className="px-12 py-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-2">Truck-wise allocations</div>
                        <table className="w-full text-xs">
                          <thead className="text-brand-muted"><tr><th className="text-left py-1">Date</th><th className="text-left py-1">Vehicle</th><th className="text-right py-1">Amount Paid</th></tr></thead>
                          <tbody>
                            {allocs.map((a, i) => (
                              <tr key={i}><td className="py-1">{formatDate(a.entry_date)}</td><td className="py-1 font-medium">{a.vehicle_number || a.truck_id?.slice(0, 8)}</td><td className="py-1 text-right tabular-nums">{inr(a.amount)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {form.id ? "Apply On-Account Balance" : "Record Payment Made"}
            </DialogTitle>
            {form.id && (
              <div className="text-xs text-brand-muted">
                Allocate the remaining on-account balance of this payment against outstanding trucks. Payment amount and bank are locked.
              </div>
            )}
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className={`grid grid-cols-2 gap-4 ${form.id ? "opacity-70" : ""}`}>
              <div>
                <Label>Transporter *</Label>
                <Select value={form.transporter_id || "none"} onValueChange={(v) => setForm({ ...form, transporter_id: v === "none" ? "" : v, allocations: [] })} disabled={!!form.id}>
                  <SelectTrigger data-testid="pm-transporter-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Free text below —</SelectItem>
                    {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>OR Transporter (free text)</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.transporter_name} onChange={(e) => setForm({ ...form, transporter_name: e.target.value })} placeholder="If not in list" disabled={!!form.id} /></div>
              <div><Label>Date</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={!!form.id} /></div>
              <div><Label>Amount *</Label><Input data-testid="pm-amount" type="number" step="0.01" className="mt-1.5 h-10 rounded-sm bg-white tabular-nums" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} disabled={!!form.id} /></div>
              <div><Label>Mode</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} disabled={!!form.id} /></div>
              <div><Label>Particulars</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.particulars} onChange={(e) => setForm({ ...form, particulars: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Bank Account (paying from)</Label>
                <BankSelector value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} testId="made-bank-selector" />
              </div>
            </div>

            {form.transporter_id && (
              <div className="border-t border-brand-line pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Allocate to truck trips (optional)</div>
                  <div className="text-xs">
                    Allocated <span className="font-semibold tabular-nums">{inr(allocTotal)}</span> · On account <span className={`font-semibold tabular-nums ${onAccount < 0 ? "text-brand-terracotta" : "text-brand-sand"}`}>{inr(onAccount)}</span>
                  </div>
                </div>
                {outstandingTrucks.length === 0 ? (
                  <div className="text-xs text-brand-muted p-4 text-center bg-secondary/40 rounded-sm">No outstanding truck trips for this transporter — payment will be on-account.</div>
                ) : (
                  <div className="border border-brand-line rounded-sm">
                    <table className="w-full text-xs">
                      <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                        <tr><th className="px-2 py-2 w-8"></th><th className="text-left px-2 py-2">Date</th><th className="text-left px-2 py-2">Vehicle</th><th className="text-left px-2 py-2">Party</th><th className="text-left px-2 py-2">Material</th><th className="text-right px-2 py-2">Total Owed</th><th className="text-right px-2 py-2">Balance</th><th className="text-right px-2 py-2 w-32">Pay Amount</th></tr>
                      </thead>
                      <tbody>
                        {outstandingTrucks.map((t) => {
                          const alloc = form.allocations.find((a) => a.truck_id === t.id);
                          const checked = !!alloc;
                          return (
                            <tr key={t.id} className={`border-t border-brand-line ${checked ? "bg-brand-sand/10" : ""}`}>
                              <td className="px-2 py-2"><Checkbox checked={checked} onCheckedChange={(v) => toggleAlloc(t, !!v)} data-testid={`alloc-truck-${t.id}`} /></td>
                              <td className="px-2 py-2">{formatDate(t.entry_date)}</td>
                              <td className="px-2 py-2 font-medium">{t.vehicle_number}</td>
                              <td className="px-2 py-2 text-brand-muted">{pname(t.party_id)}</td>
                              <td className="px-2 py-2 text-brand-muted">{mname(t.material_id) || "—"}</td>
                              <td className="px-2 py-2 text-right tabular-nums">{inr(t.owed)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-brand-terracotta">{inr(t.balance)}</td>
                              <td className="px-2 py-2 text-right">
                                {checked && (
                                  <Input type="number" step="0.01" value={alloc.amount}
                                    onChange={(e) => updateAllocAmt(t.id, e.target.value)}
                                    className="h-8 w-28 rounded-sm text-right tabular-nums bg-white text-xs ml-auto" />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-payment-made-btn" className="rounded-sm bg-brand-forest text-white">
              {form.id ? "Apply Allocations" : "Save Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettleDialog
        open={settleOpen} onOpenChange={setSettleOpen}
        title="Settle Truck Trips from On-Account"
        entityLabel="Transporter" entities={transporters} initialEntityId={settleTrans}
        testIdPrefix="settle-made" allocKey="truck_id"
        endpoint={(tid) => `/transporter/${tid}/settle`}
        loadRows={async (tid) => {
          const { data } = await api.get(`/transporter/${tid}/trucks-detail`);
          const rows = data.trucks.filter((t) => t.balance > 0.01)
            .sort((a, b) => (a.entry_date < b.entry_date ? -1 : 1))
            .map((t) => ({ id: t.truck_id, balance: t.balance, entry_date: t.entry_date, vehicle_number: t.vehicle_number, party_name: t.party_name, material_name: t.material_name, total_owed: t.total_owed, paid: t.paid }));
          return { onAccount: data.on_account, rows };
        }}
        columns={[
          { key: "entry_date", label: "Date", render: (r) => formatDate(r.entry_date) },
          { key: "vehicle_number", label: "Vehicle", className: "font-medium" },
          { key: "party_name", label: "Party", className: "text-brand-muted" },
          { key: "material_name", label: "Material", className: "text-brand-muted", render: (r) => r.material_name || "—" },
          { key: "total_owed", label: "Owed", align: "right", render: (r) => inr(r.total_owed) },
          { key: "paid", label: "Paid", align: "right", render: (r) => inr(r.paid) },
        ]}
        onDone={load}
      />
    </div>
  );
}
